import type {
  Payee,
  PayeeSite,
} from "components/payment-configuration/endpoints";

export interface Prerequisite {
  id: string;
  label: string;
  description: string;
  required: boolean;
}

export interface PrerequisiteSelection {
  value: string;
}

export const UPLOAD_NEW_FILE_VALUE = "__upload_new_file__";

export interface IUploadPrerequisiteModalProps {
  visible: boolean;
  prerequisiteLabel: string;
  onDismiss: () => void;
  onUseFile: (file: File) => void;
}

export interface StagedPrerequisiteFile {
  prerequisiteId: string;
  documentType: string;
  file: File;
}

export interface Watcher {
  alias: string;
  fullName: string;
  jobTitle: string;
  org: string;
}

export interface PaymentLine {
  id: string;
  name: string;
  type: string;
  gl: string;
  taxType: string;
  dueAmount: string;
  dffExpenseAccount: string;
  taxTypeCode: string;
}

export interface PaymentHeader {
  id: string;
  invoiceNumber: string;
  paymentDescription: string;
  paymentCurrency: string;
  entityRegistrationNumber: string;
  vendorName: string;
  vendorNumber: string;
  payeeSiteId: string;
  paymentMethod: string;
  period: string;
  lines: PaymentLine[];
  // Set by a payee lookup; read-only until cleared.
  payeeFields?: PayeeField[];
  // Set when the user types one; autofill then skips the header.
  invoiceNumberEdited?: boolean;
}

export type PayeeField =
  | "vendorName"
  | "vendorNumber"
  | "payeeSiteId"
  | "paymentMethod"
  | "paymentCurrency";

// A payee flattened with one of its sites.
export type PayeeRow = Omit<Payee, "payeeSiteList"> &
  Partial<PayeeSite> & { key: string };

export const DEFAULT_PAYMENT_CURRENCY = "USD";
export const DEFAULT_PAYMENT_METHOD = "EFT";
export const DEFAULT_LINE_TYPE = "Tax";
export const DEFAULT_LINE_TAX_TYPE = "Sales Tax";

export const PAYMENT_CURRENCY_OPTIONS = ["USD", "CAD", "EUR", "GBP"];
export const PAYMENT_METHOD_OPTIONS = ["EFT", "Check", "Wire"];

export const CLEARED_PAYEE_FIELDS: Record<PayeeField, string> = {
  vendorName: "",
  vendorNumber: "",
  payeeSiteId: "",
  paymentMethod: DEFAULT_PAYMENT_METHOD,
  paymentCurrency: DEFAULT_PAYMENT_CURRENCY,
};
export const LINE_TYPE_OPTIONS = ["Tax", "Penalty", "Interest"];
export const LINE_TAX_TYPE_OPTIONS = ["Sales Tax", "Use Tax", "VAT", "GST/HST"];

let idSeed = 0;
const nextId = (prefix: string): string => {
  idSeed += 1;
  return `${prefix}-${idSeed}`;
};

export const parseAmount = (value: string): number => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatCurrency = (amount: number): string =>
  `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// EFT description pattern: TXB*[Registration]*[Tax Code]*[Period]*[Total no decimals]
export const computeEftDescription = (header: PaymentHeader): string => {
  const registration = header.entityRegistrationNumber || "";
  const taxCode = header.lines[0]?.taxTypeCode || "";
  const period = header.period || "";
  const total = Math.round(computeHeaderTotal(header) * 100);
  return `TXB*${registration}*${taxCode}*${period}*${total}`;
};

export const computeHeaderTotal = (header: PaymentHeader): number =>
  header.lines.reduce((sum, line) => sum + parseAmount(line.dueAmount), 0);

export const computeTotalPaymentAmount = (headers: PaymentHeader[]): number =>
  headers.reduce((sum, header) => sum + computeHeaderTotal(header), 0);

export const createPaymentLine = (
  defaults?: Partial<PaymentLine>,
): PaymentLine => ({
  id: nextId("line"),
  name: "",
  type: defaults?.type ?? DEFAULT_LINE_TYPE,
  gl: defaults?.gl ?? "",
  taxType: defaults?.taxType ?? DEFAULT_LINE_TAX_TYPE,
  dueAmount: "",
  dffExpenseAccount: "",
  taxTypeCode: "",
});

export const createPaymentHeader = (
  defaults?: Partial<PaymentHeader>,
): PaymentHeader => ({
  id: nextId("header"),
  invoiceNumber: "",
  paymentDescription: "",
  paymentCurrency: defaults?.paymentCurrency ?? DEFAULT_PAYMENT_CURRENCY,
  entityRegistrationNumber: defaults?.entityRegistrationNumber ?? "",
  vendorName: defaults?.vendorName ?? "",
  vendorNumber: defaults?.vendorNumber ?? "",
  payeeSiteId: defaults?.payeeSiteId ?? "",
  paymentMethod: defaults?.paymentMethod ?? DEFAULT_PAYMENT_METHOD,
  period: defaults?.period ?? "",
  lines: [createPaymentLine(defaults?.lines?.[0])],
  payeeFields: defaults?.payeeFields,
});
